import {
  RiskManagementSummaryDocument,
  RiskManagementTableDocument,
  type RiskManagementTable,
  type RiskManagementTableQuery,
  type RiskManagementTableQueryVariables,
  type RiskManagementSummaryQuery,
  type RiskManagementSummaryQueryVariables,
} from "$/__generated__/graphql";
import { RiskDataTables } from "components/tables";
import {
  RISK_MANAGEMENT_TABLE_BASE_DEFS,
  RISK_MANAGEMENT_SUMMARY_ROUNDING,
} from "constants/column-defs";
import { useGlobalFiltersContext } from "contexts/filters/global-filters";
import { useOutcomeScenariosContext } from "contexts/scenarios";
import {
  useDataTable,
  useDataTableSummaries,
  useSummariesFormatters,
} from "hooks/queries";

/*
  [WiP] Risk summaries footer on platform ui needs to handle
  pinned groups with multiple grouped columns.

  Right now it takes the first one (based on risk summaries)
  and stretches the inputs out to it, but risk summmaries
  use two, pinned header groups. make it dynamic
 */

export const RiskManagementDataTable = () => {
  const { hierarchyIdsRisk } = useGlobalFiltersContext();
  const { parsedScenario } = useOutcomeScenariosContext();

  const [failureModeId = 0, failureEffectId = 0] = parsedScenario ?? [];
  const skip = parsedScenario?.length !== 2;

  const {
    showFoot,
    riskSystemMetric,
    setRiskSystemMetric,
    riskSelectionMetric,
    setRiskSelectionMetric,
    setShowFoot,
    loading,
    isFetchingMore,
    rows,
    fetchMoreDataTable,
  } = useDataTable<
    RiskManagementTable,
    RiskManagementTableQuery,
    RiskManagementTableQueryVariables
  >("riskManagementTable", RiskManagementTableDocument, {
    skip,
    variables: {
      hierarchyIds: hierarchyIdsRisk,
      failureModeId,
      failureEffectId,
    },
  });

  const { columns } = useDataTableSummaries<
    RiskManagementTable,
    RiskManagementSummaryQuery,
    RiskManagementSummaryQueryVariables
  >(
    RISK_MANAGEMENT_TABLE_BASE_DEFS,
    RiskManagementSummaryDocument,
    {
      skip,
      variables: {
        hierarchyIds: hierarchyIdsRisk,
        selectionMetricType: riskSelectionMetric,
        systemMetricType: riskSystemMetric,
        failureModeId,
        failureEffectId,
      },
    },
    useSummariesFormatters(
      riskSelectionMetric,
      riskSystemMetric,
      RISK_MANAGEMENT_SUMMARY_ROUNDING,
    ),
  );

  return (
    <RiskDataTables<RiskManagementTable>
      loading={loading}
      data={rows}
      columns={columns}
      selectionValue={riskSelectionMetric}
      onSelectionChange={setRiskSelectionMetric}
      systemValue={riskSystemMetric}
      onSystemChange={setRiskSystemMetric}
      showFoot={showFoot}
      setShowFoot={setShowFoot}
      onFetchMore={fetchMoreDataTable}
      isFetchingMore={isFetchingMore}
    />
  );
};

export default RiskManagementDataTable;
